import { InputContainer } from '@creditornot/cb-form';
import { Menu } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FieldArrayRenderProps } from 'react-final-form-arrays';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { SubcategoryInputField } from './SubcategoryInputField';
import { AddSubcategoriesFormValues } from '../BulkAddSubcategoriesModal/types';
import { CreateCategoryFormValues } from '../CreateCategoryForm/types';
import { SortableContainer } from '../SortableContainer';

type SubcategoryValue = { name: string; _rowId: string };

type SubcategoryInputFieldsProps<T> = {
  subcategories: SubcategoryValue[];
  onChange: (data: SubcategoryValue[]) => void;
  fields: FieldArrayRenderProps<T, HTMLElement>['fields'];
};

const StyledInputContainer = styled(InputContainer)`
  margin-block-start: 16px;
  & + & {
    margin-block-start: 8px;
  }
`;

const useTouchedRowIdSet = () => {
  const [touchedFieldRowId, setTouchedFieldRowId] = useState<string[]>([]);
  return {
    touchedRowId: (rowId: string) => touchedFieldRowId.includes(rowId),
    addTouchedId: (rowId: string) => {
      if (!touchedFieldRowId.includes(rowId)) {
        setTouchedFieldRowId(ids => ids.concat(rowId));
      }
    },
    removeTouchedRowId: (rowId: string) => {
      setTouchedFieldRowId(ids => ids.filter(id => id !== rowId));
    },
  };
};

export const SubcategoryInputFields = <
  T extends CreateCategoryFormValues['childCategoriesRecord'] | AddSubcategoriesFormValues['childCategoriesRecord'],
>({
  subcategories,
  onChange,
  fields,
}: SubcategoryInputFieldsProps<T>) => {
  const intl = useIntl();
  const { removeTouchedRowId, addTouchedId, touchedRowId } = useTouchedRowIdSet();
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 10 } }));
  const [draggingInputRowId, setDraggingInputRowId] = useState<UniqueIdentifier | null>(null);

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDraggingInputRowId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = subcategories.findIndex(childCategory => childCategory._rowId === active.id);
      const newIndex = subcategories.findIndex(childCategory => childCategory._rowId === over.id);

      if (oldIndex === -1 && newIndex === -1) {
        return;
      }

      onChange(arrayMove(subcategories, oldIndex, newIndex));
    },
    [subcategories, onChange],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingInputRowId(event.active.id);
  }, []);

  const getChildCategoryRowId = useCallback(
    (childCategoriesIndex: number) => subcategories[childCategoriesIndex]._rowId,
    [subcategories],
  );

  const draggingChildCategory = useMemo(
    () => subcategories.find(childCategory => childCategory._rowId === draggingInputRowId),
    [subcategories, draggingInputRowId],
  );

  const itemUniqueIdentifiers = useMemo(
    () => fields.map((_, childCategoriesIndex) => getChildCategoryRowId(childCategoriesIndex)),
    [fields, getChildCategoryRowId],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext items={itemUniqueIdentifiers} strategy={verticalListSortingStrategy}>
        {fields.map((name, subcategoryIndex) => {
          const rowId = getChildCategoryRowId(subcategoryIndex);

          return (
            <SortableContainer id={rowId} key={rowId}>
              <StyledInputContainer>
                <SubcategoryInputField
                  addTouchedId={addTouchedId}
                  canDelete={(fields.length || 0) > 1}
                  subcategoryIndex={subcategoryIndex}
                  subcategories={subcategories}
                  onChange={onChange}
                  fieldName={`${name}.name`}
                  placeholder={intl.formatMessage({
                    id: 'menu.bulkCreateCategories.addCategory.subcategoryInputPlaceholder',
                  })}
                  removeTouchedRowId={removeTouchedRowId}
                  rowId={rowId}
                  touched={touchedRowId(rowId)}
                />
              </StyledInputContainer>
            </SortableContainer>
          );
        })}
      </SortableContext>

      {createPortal(
        // Ensure drag overlay stay on top of wolt-modal
        <DragOverlay zIndex={999999}>
          {draggingChildCategory && (
            <Input
              icon={<Menu />}
              value={draggingChildCategory.name}
              readOnly
              placeholder={intl.formatMessage({
                id: 'menu.bulkCreateCategories.addCategory.subcategoryInputPlaceholder',
              })}
            />
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
